import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { CATEGORIES, DEMO_LISTINGS, MIN_HIRE_OPTIONS, formatCurrencyByCountry } from '../lib/constants'
import { MAX_BOOKING_DAYS, formatHirePeriod } from '../lib/hirePricing'
import Header from '../components/Header'
import AvailabilityCalendar from '../components/AvailabilityCalendar'
import VerifyEmailPrompt from '../components/VerifyEmailPrompt'
import { prepareImageForUpload } from '../lib/imageUpload'
import ListingPlaceholder from '../components/ListingPlaceholder'
import { LISTING_REMOVAL_REASONS } from '../lib/removalReasons'
import ContactWarning from '../components/ContactWarning'
import { checkAndLogContact } from '../lib/contactFlags'

const DEMO_MODE = process.env.NODE_ENV === 'development'

const MONTH_SHORT = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC']

// Mini calendar icon showing today's date
function CalendarIcon() {
const now = new Date()
return (
<span style={{
display: 'inline-flex',
flexDirection: 'column',
alignItems: 'center',
background: 'white',
border: '1.5px solid var(--primary)',
borderRadius: 4,
overflow: 'hidden',
width: 22,
height: 22,
boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
flexShrink: 0,
}}>
<span style={{
background: 'linear-gradient(135deg, #2D3FCC 0%, #7B3FE4 100%)',
color: 'white',
fontSize: 7,
fontWeight: 700,
width: '100%',
textAlign: 'center',
lineHeight: '8px',
paddingTop: 1,
letterSpacing: '0.5px',
}}>
{MONTH_SHORT[now.getMonth()]}
</span>
<span style={{
flex: 1,
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
fontSize: 11,
fontWeight: 700,
color: 'var(--navy)',
lineHeight: 1,
}}>
{now.getDate()}
</span>
</span>
)
}

export default function MyListings() {
const { user, profile } = useAuth()
const navigate = useNavigate()
const [listings, setListings] = useState([])
const [loading, setLoading] = useState(true)
const [editModal, setEditModal] = useState(null)
const [editForm, setEditForm] = useState({})
const [editImages, setEditImages] = useState([])
const [saving, setSaving] = useState(false)
const [uploadingPhoto, setUploadingPhoto] = useState(false)
const [deleteModal, setDeleteModal] = useState(null)
const [removalReason, setRemovalReason] = useState('')
const [availabilityModal, setAvailabilityModal] = useState(null)
const fileRef = useRef()

useEffect(() => {
if (!user && !DEMO_MODE) return // ProtectedRoute sends logged-out visitors to sign-in
fetchListings()
}, [])

// /my-listings?edit=<id> (used by the "add a photo" email) opens that listing's edit window
const [searchParams, setSearchParams] = useSearchParams()
const editParam = searchParams.get('edit')
useEffect(() => {
if (!editParam || loading) return
const target = listings.find(l => l.id === editParam)
if (target) openEdit(target)
setSearchParams({}, { replace: true })
// eslint-disable-next-line
}, [editParam, loading, listings])

const fetchListings = async () => {
setLoading(true)
if (DEMO_MODE) { setListings(DEMO_LISTINGS.slice(0,4)); setLoading(false); return }
const { data } = await supabase.from('listings').select('*').eq('owner_id', user.id).is('archived_at', null).order('created_at', { ascending: false })
setListings(data || [])
setLoading(false)
}

const toggleAvailable = async (listing) => {
await supabase.from('listings').update({ available: !listing.available }).eq('id', listing.id)
fetchListings()
}

const deleteListing = async (id) => {
const { data: bookings } = await supabase.from('bookings').select('id, status').eq('listing_id', id)
const allowedStatuses = ['enquiry', 'pending', 'cancelled', 'completed']
const hasBlockingBookings = bookings?.some(b => !allowedStatuses.includes(b.status))
if (hasBlockingBookings) {
alert("You can't remove this listing while it has active or in-progress hires. Once all bookings are completed, you'll be able to remove it.")
return
}
const { error } = await supabase.from('listings').update({ archived_at: new Date().toISOString(), removed_by: 'owner', removal_reason: removalReason || null }).eq('id', id)
if (!error) {
setDeleteModal(null)
setListings(prev => prev.filter(l => l.id !== id))
} else {
alert('Failed to remove listing. Please try again.')
}
}

const openEdit = (listing) => {
setEditForm({
title: listing.title || '',
description: listing.description || '',
price_per_hour: listing.price_per_hour || '',
price_per_day: listing.price_per_day || '',
price_per_week: listing.price_per_week || '',
deposit: listing.deposit || '',
min_hours: listing.min_hours || 1,
rules: listing.rules || '',
category_id: listing.category_id || '',
includes_operator: listing.includes_operator || false,
})
setEditImages(listing.images || [])
setEditModal(listing)
}

const handleAddPhoto = async (e) => {
const file = e.target.files?.[0]
if (!file || !editModal) return
setUploadingPhoto(true)

let fileToUpload
try {
fileToUpload = await prepareImageForUpload(file)
} catch (prepErr) {
console.error('Image prep failed:', prepErr)
alert('Could not process this image. Try a JPEG or PNG instead.')
setUploadingPhoto(false)
return
}

const fileName = `${editModal.id}-${Date.now()}.jpg`
const { error } = await supabase.storage.from('listing-images').upload(fileName, fileToUpload, { cacheControl: '31536000', upsert: true, contentType: 'image/jpeg' })
if (!error) {
const { data } = supabase.storage.from('listing-images').getPublicUrl(fileName)
const newImages = [...editImages, data.publicUrl]
setEditImages(newImages)
}
setUploadingPhoto(false)
}
const handleRemovePhoto = (index) => {
const newImages = editImages.filter((_, i) => i !== index)
setEditImages(newImages)
}

const handleEditSave = async () => {
if (!editModal) return
checkAndLogContact('listing', `${editForm.title}\n${editForm.description}`, editModal.id)
const hourlyPrice = editForm.price_per_hour ? parseFloat(editForm.price_per_hour) : null
const dailyPrice = editForm.price_per_day ? parseFloat(editForm.price_per_day) : null
const weeklyPrice = editForm.price_per_week ? parseFloat(editForm.price_per_week) : null
if (!hourlyPrice && !dailyPrice && !weeklyPrice) { alert('Please set at least one rate (hourly, daily, or weekly).'); return }
if (editForm.price_per_hour && (!hourlyPrice || hourlyPrice < 10)) { alert('Hourly rate must be at least $10.'); return }
if ((parseInt(editForm.min_hours) || 1) > MAX_BOOKING_DAYS * 24) { alert(`Minimum hire can be at most ${MAX_BOOKING_DAYS} days — bookings are limited to ${MAX_BOOKING_DAYS} days, so a longer minimum means nobody can book it.`); return }
if (dailyPrice && hourlyPrice && dailyPrice < hourlyPrice) { alert(`Daily rate can't be less than your hourly rate ($${hourlyPrice.toFixed(2)}).`); return }
if (weeklyPrice) {
const floor = dailyPrice || hourlyPrice
if (floor && weeklyPrice < floor) {
const floorLabel = dailyPrice ? 'daily rate' : 'hourly rate'
alert(`Weekly rate can't be less than your ${floorLabel} ($${floor.toFixed(2)}).`); return
}
}
setSaving(true)
await supabase.from('listings').update({
title: editForm.title,
description: editForm.description,
price_per_hour: editForm.price_per_hour ? parseFloat(editForm.price_per_hour) : null,
price_per_day: editForm.price_per_day ? parseFloat(editForm.price_per_day) : null,
price_per_week: editForm.price_per_week ? parseFloat(editForm.price_per_week) : null,
deposit: parseFloat(editForm.deposit) || 0,
min_hours: parseInt(editForm.min_hours) || 1,
rules: editForm.rules,
category_id: parseInt(editForm.category_id),
includes_operator: editForm.includes_operator || false,
images: editImages,
}).eq('id', editModal.id)
setSaving(false)
setEditModal(null)
fetchListings()
}

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 700, padding: '1.5rem 1rem' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
<button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← Back</button>
<h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 0, color: 'var(--navy)' }}>My listings</h1>
<button className="btn btn-gradient" style={{ marginLeft: 'auto' }} onClick={() => navigate('/list-item')}>+ New listing</button>
</div>

{!loading && listings.length > 0 && profile && !profile.email_verified && (
<VerifyEmailPrompt reason="my_listings" style={{ marginBottom: '1rem' }} />
)}

{loading ? <div className="spinner-wrap"><div className="spinner" /></div> :
listings.length === 0 ? (
<div style={{ textAlign: 'center', padding: '3rem' }}>
<div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
<p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>You haven't listed anything yet.</p>
<button className="btn btn-gradient" onClick={() => navigate('/list-item')}>List your first item</button>
</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
{listings.map(l => {
const cat = CATEGORIES.find(c => c.id === l.category_id)
return (
<div key={l.id} className="card card-body">
<div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
{l.images?.[0] ? (
<img src={l.images[0]} alt={l.title} loading="lazy" style={{ width: 52, height: 52, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
) : (
<ListingPlaceholder listing={l} size={52} radius={12} />
)}
<div style={{ flex: 1 }}>
<div style={{ fontWeight: 600, marginBottom: 2, cursor: 'pointer', color: 'var(--primary)', textDecoration: 'none' }} onClick={() => navigate(`/listing/${l.id}`)} onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>{l.title}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
{[
cat?.name,
`${l.suburb}, ${l.postcode}`,
l.price_per_hour && `${formatCurrencyByCountry(l.price_per_hour, l.country || 'Australia')}/hr`,
l.price_per_day && `${formatCurrencyByCountry(l.price_per_day, l.country || 'Australia')}/day`,
l.price_per_week && `${formatCurrencyByCountry(l.price_per_week, l.country || 'Australia')}/week`,
].filter(Boolean).join(' · ')}
</div>
{l.admin_disabled && (
<span className="badge badge-red" style={{ fontSize: 11, marginTop: 4, marginRight: 6 }}>
🚫 Disabled by HireIt
</span>
)}
<span className={`badge ${l.available ? 'badge-green' : 'badge-amber'}`} style={{ fontSize: 11, marginTop: 4 }}>
{l.available ? 'Available' : 'Unavailable'}
</span>
</div>
</div>

{/* Action buttons row */}
<div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
<button className="btn btn-ghost btn-sm" onClick={() => openEdit(l)}>✏️ Edit</button>
<button
className="btn btn-ghost btn-sm"
onClick={() => setAvailabilityModal(l)}
style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
>
<CalendarIcon />
Manage availability
</button>
<button
className={`btn btn-sm ${l.available ? 'btn-ghost' : 'btn-primary'}`}
onClick={() => toggleAvailable(l)}
>
{l.available ? '⏸ Mark unavailable' : '▶ Mark available'}
</button>
<button className="btn btn-danger btn-sm" onClick={() => { setRemovalReason(''); setDeleteModal(l.id) }} style={{ marginLeft: 'auto' }}>Delete</button>
</div>
</div>
)
})}
</div>
)}
</div>

{/* Availability modal */}
{availabilityModal && (
<div className="modal-overlay" onClick={() => setAvailabilityModal(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
<h3 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 10 }}>
<CalendarIcon />
Manage availability
</h3>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
<strong>{availabilityModal.title}</strong>
</p>
<AvailabilityCalendar listingId={availabilityModal.id} mode="owner" />
<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
<button className="btn btn-gradient" onClick={() => setAvailabilityModal(null)}>Done</button>
</div>
</div>
</div>
)}

{deleteModal && (
<div className="modal-overlay" onClick={() => setDeleteModal(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 600, marginBottom: 8 }}>Delete listing</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>
Are you sure you want to delete this listing? This cannot be undone.
</p>
<div className="form-group" style={{ marginBottom: 20 }}>
<label className="form-label">Why are you removing it? <span style={{ fontWeight: 400, color: 'var(--text-faint)' }}>(optional)</span></label>
<select className="form-control" value={removalReason} onChange={e => setRemovalReason(e.target.value)}>
<option value="">Prefer not to say</option>
{LISTING_REMOVAL_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
</select>
</div>
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
<button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
<button className="btn btn-danger" onClick={() => deleteListing(deleteModal)}>Yes, delete</button>
</div>
</div>
</div>
)}

{editModal && (
<div className="modal-overlay" onClick={() => setEditModal(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
<h3 style={{ fontWeight: 600, marginBottom: 16 }}>✏️ Edit listing</h3>

<div className="form-group">
<label className="form-label">Photos</label>
<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
{editImages.map((img, i) => (
<div key={i} style={{ position: 'relative' }}>
<img src={img} alt="" loading="lazy" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }} />
<button
onClick={() => handleRemovePhoto(i)}
style={{ position: 'absolute', top: -6, right: -6, background: 'var(--red)', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
>×</button>
</div>
))}
{editImages.length < 5 && (
<button
onClick={() => fileRef.current?.click()}
style={{ width: 80, height: 80, border: '2px dashed var(--border)', borderRadius: 8, background: 'var(--gray-50)', cursor: 'pointer', fontSize: 24, color: 'var(--text-muted)' }}
disabled={uploadingPhoto}
>
{uploadingPhoto ? '...' : '+'}
</button>
)}
</div>
<input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAddPhoto} />
</div>

<div className="form-group">
<label className="form-label">Title</label>
<input className="form-control" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
</div>

<div className="form-group">
<label className="form-label">Category</label>
<select className="form-control" value={editForm.category_id} onChange={e => setEditForm(f => ({ ...f, category_id: e.target.value }))}>
{CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
</select>
</div>

<div className="form-group">
<label className="form-label">Description</label>
<textarea className="form-control" value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} style={{ minHeight: 100 }} />
</div>

<div className="form-row">
<div className="form-group">
<label className="form-label">Price per hour ($) <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
<input className="form-control" type="number" value={editForm.price_per_hour} onChange={e => setEditForm(f => ({ ...f, price_per_hour: e.target.value }))} />
</div>
<div className="form-group">
<label className="form-label">Security deposit ($)</label>
<input className="form-control" type="number" value={editForm.deposit} onChange={e => setEditForm(f => ({ ...f, deposit: e.target.value }))} />
</div>
</div>

<div className="form-row">
<div className="form-group">
<label className="form-label">Price per day (optional)</label>
<input className="form-control" type="number" placeholder="0.00" value={editForm.price_per_day} onChange={e => setEditForm(f => ({ ...f, price_per_day: e.target.value }))} />
</div>
<div className="form-group">
<label className="form-label">Price per week (optional)</label>
<input className="form-control" type="number" placeholder="0.00" value={editForm.price_per_week} onChange={e => setEditForm(f => ({ ...f, price_per_week: e.target.value }))} />
</div>
</div>

<div className="form-group">
<label className="form-label">Minimum hire period</label>
<select className="form-control" value={editForm.min_hours} onChange={e => setEditForm(f => ({ ...f, min_hours: parseInt(e.target.value) }))}>
{/* Older listings may have a minimum above the cap — show it so the owner can see what to change */}
{(parseInt(editForm.min_hours) || 1) > MAX_BOOKING_DAYS * 24 && (
<option value={editForm.min_hours}>{formatHirePeriod(editForm.min_hours)} (too long — choose {MAX_BOOKING_DAYS} days or less)</option>
)}
{!MIN_HIRE_OPTIONS.some(o => o.value === parseInt(editForm.min_hours)) && (parseInt(editForm.min_hours) || 1) <= MAX_BOOKING_DAYS * 24 && (
<option value={editForm.min_hours}>{formatHirePeriod(editForm.min_hours)}</option>
)}
{(editForm.price_per_hour ? MIN_HIRE_OPTIONS : MIN_HIRE_OPTIONS.filter(o => o.value >= 24)).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
</select>
{(parseInt(editForm.min_hours) || 1) > MAX_BOOKING_DAYS * 24
? <div className="form-error">Bookings are limited to {MAX_BOOKING_DAYS} days, so nobody can book this listing until the minimum is {MAX_BOOKING_DAYS} days or less.</div>
: <div className="form-hint">Shortest hire you'll accept (up to {MAX_BOOKING_DAYS} days)</div>}
</div>

<div className="form-group">
<label className="form-label">Hire rules (optional)</label>
<textarea className="form-control" value={editForm.rules} onChange={e => setEditForm(f => ({ ...f, rules: e.target.value }))} style={{ minHeight: 70 }} />
</div>

<div className="form-group">
<label className="form-label">Operator included (optional)</label>
<label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: `1px solid ${editForm.includes_operator ? 'var(--primary)' : 'var(--border)'}`, background: editForm.includes_operator ? 'var(--primary-light)' : 'white' }}>
<input type="checkbox" checked={editForm.includes_operator || false} onChange={e => setEditForm(f => ({ ...f, includes_operator: e.target.checked }))} style={{ marginTop: 2, flexShrink: 0 }} />
<span>🛠️ <strong>This listing includes me as the operator</strong> — turn this on if you'll be operating the equipment yourself as part of the hire.</span>
</label>
</div>

<ContactWarning text={`${editForm.title}\n${editForm.description}`} style={{ marginTop: 16, marginBottom: 0 }} />
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
<button className="btn btn-ghost" onClick={() => setEditModal(null)}>Cancel</button>
<button className="btn btn-gradient" onClick={handleEditSave} disabled={saving}>
{saving ? 'Saving...' : 'Save changes'}
</button>
</div>
</div>
</div>
)}
</div>
)
}